import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { babyApi, eventApi, photoApi } from '../services/api';
import { getBabyKey, decryptBabyProfile, decryptEventData, decryptData, createDecryptedImageUrl } from '../crypto';
import { formatDistanceToNow, startOfDay, endOfDay, subDays, format, differenceInWeeks } from 'date-fns';
import type { Baby, DecryptedBaby, ProfileType } from '../types';
import { useTimers, ActiveTimer } from '../stores/useTimers';
import { useBabyStore } from '../stores/useBaby';
import TimerWidget from '../components/TimerWidget';
import QuickTimerButton from '../components/QuickTimerButton';

interface DailyStats {
  feeds: number;
  nappies: number;
  sleepMinutes: number;
  totalFeedMl: number;
  lastFeed: Date | null;
  lastNappy: Date | null;
  lastSleep: Date | null;
}

const EVENT_TYPE_INFO: Record<string, { icon: string; label: string; color: string }> = {
  FEED: { icon: '🍼', label: 'Feed', color: 'bg-blue-100 dark:bg-blue-900' },
  NAPPY: { icon: '🧷', label: 'Nappy', color: 'bg-yellow-100 dark:bg-yellow-900' },
  SPIT_UP: { icon: '💧', label: 'Spit Up', color: 'bg-orange-100 dark:bg-orange-900' },
  MEDICATION: { icon: '💊', label: 'Medication', color: 'bg-red-100 dark:bg-red-900' },
  SLEEP: { icon: '😴', label: 'Sleep', color: 'bg-purple-100 dark:bg-purple-900' },
  WEIGHT: { icon: '⚖️', label: 'Weight', color: 'bg-green-100 dark:bg-green-900' },
  TEMPERATURE: { icon: '🌡️', label: 'Temperature', color: 'bg-pink-100 dark:bg-pink-900' },
  TUMMY_TIME: { icon: '🏋️', label: 'Tummy Time', color: 'bg-teal-100 dark:bg-teal-900' },
  PUMP: { icon: '🥛', label: 'Pump', color: 'bg-indigo-100 dark:bg-indigo-900' },
  BOTTLE: { icon: '🍶', label: 'Bottle', color: 'bg-cyan-100 dark:bg-cyan-900' },
  JOURNAL: { icon: '📝', label: 'Journal', color: 'bg-gray-100 dark:bg-gray-800' },
  EC: { icon: '🚽', label: 'EC Time', color: 'bg-emerald-100 dark:bg-emerald-900' },
  // Pregnancy event types
  SYMPTOM: { icon: '🤢', label: 'Symptom', color: 'bg-orange-100 dark:bg-orange-900' },
  APPOINTMENT: { icon: '🏥', label: 'Appointment', color: 'bg-blue-100 dark:bg-blue-900' },
  BLOOD_PRESSURE: { icon: '🩺', label: 'Blood Pressure', color: 'bg-red-100 dark:bg-red-900' },
  ULTRASOUND: { icon: '📷', label: 'Ultrasound', color: 'bg-purple-100 dark:bg-purple-900' },
  KICK_COUNT: { icon: '🦶', label: 'Kick Count', color: 'bg-pink-100 dark:bg-pink-900' },
  CONTRACTION: { icon: '⏱️', label: 'Contraction', color: 'bg-rose-100 dark:bg-rose-900' },
};

// Secondary event types shown when expanded (FEED, NAPPY, SLEEP are handled by timer buttons)
const SECONDARY_EVENT_TYPES = ['BOTTLE', 'MEDICATION', 'TEMPERATURE', 'WEIGHT', 'PUMP', 'SPIT_UP', 'EC', 'JOURNAL'];

export default function Dashboard() {
  const navigate = useNavigate();
  const { selectedBabyId, setSelectedBabyId } = useBabyStore();
  const [decryptedBabies, setDecryptedBabies] = useState<Map<string, DecryptedBaby>>(new Map());
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const { getActiveTimer, syncTimersFromBackend } = useTimers();

  // Fetch babies
  const { data: babies, isLoading: loadingBabies } = useQuery({
    queryKey: ['babies'],
    queryFn: async () => {
      const response = await babyApi.list();
      return response.data as Baby[];
    },
  });

  // Decrypt baby profiles
  useEffect(() => {
    async function decryptBabies() {
      if (!babies) {
        return;
      }

      const decrypted = new Map<string, DecryptedBaby>();

      for (const baby of babies) {
        try {
          const aesKey = await getBabyKey(baby.id);
          if (aesKey) {
            const profile = await decryptBabyProfile(baby.encryptedData, aesKey);
            decrypted.set(baby.id, {
              ...profile,
              id: baby.id,
              profileType: (profile.profileType === 'pregnancy' ? 'PREGNANCY' : 'BABY') as ProfileType,
              dateOfBirth: profile.dateOfBirth ? new Date(profile.dateOfBirth) : undefined,
              postFeedUprightMinutes: profile.postFeedUprightMinutes,
              dailyFeedTargetMinMl: profile.dailyFeedTargetMinMl ?? profile.dailyFeedTargetMl,
              dailyFeedTargetMaxMl: profile.dailyFeedTargetMaxMl ?? profile.dailyFeedTargetMl,
              role: baby.role,
              createdAt: new Date(baby.createdAt),
              updatedAt: new Date(baby.updatedAt),
            });
          }
        } catch (err) {
          console.error(`Failed to decrypt baby ${baby.id}:`, err);
        }
      }

      setDecryptedBabies(decrypted);

      // Select first baby if none selected
      if (!selectedBabyId && decrypted.size > 0) {
        const firstKey = decrypted.keys().next().value;
        if (firstKey) setSelectedBabyId(firstKey);
      }
    }

    decryptBabies();
  }, [babies, selectedBabyId, setSelectedBabyId]);

  // Load profile photo when selected baby changes
  useEffect(() => {
    async function loadProfilePhoto() {
      // Clean up previous photo URL
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
        setProfilePhotoUrl(null);
      }

      if (!selectedBabyId || !babies) return;

      const baby = babies.find(b => b.id === selectedBabyId);
      if (!baby?.profilePhotoId) return;

      try {
        const aesKey = await getBabyKey(selectedBabyId);
        if (!aesKey) return;

        // Download through backend to avoid CORS
        const { blob: encryptedBlob, mimeType } = await photoApi.downloadDirect(
          baby.profilePhotoId
        );

        // Decrypt and create URL
        const imageUrl = await createDecryptedImageUrl(
          encryptedBlob,
          aesKey,
          mimeType
        );
        setProfilePhotoUrl(imageUrl);
      } catch (err) {
        console.error('Failed to load profile photo:', err);
      }
    }

    loadProfilePhoto();

    // Cleanup on unmount
    return () => {
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
    };
  }, [selectedBabyId, babies]);

  // Sync timers from backend when baby changes
  useEffect(() => {
    if (selectedBabyId) {
      syncTimersFromBackend(selectedBabyId);
    }
  }, [selectedBabyId, syncTimersFromBackend]);

  // Fetch latest events for selected baby
  const { data: latestEvents } = useQuery({
    queryKey: ['latestEvents', selectedBabyId],
    queryFn: async () => {
      if (!selectedBabyId) return null;
      const response = await eventApi.getLatest(selectedBabyId);
      return response.data;
    },
    enabled: !!selectedBabyId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Fetch today's events for daily stats
  const { data: todayEvents } = useQuery({
    queryKey: ['todayEvents', selectedBabyId],
    queryFn: async () => {
      if (!selectedBabyId) return [];
      const todayStart = startOfDay(new Date()).toISOString();
      const todayEnd = endOfDay(new Date()).toISOString();
      const response = await eventApi.list(selectedBabyId, {
        startDate: todayStart,
        endDate: todayEnd,
        limit: 1000,
      });
      return response.data?.events || [];
    },
    enabled: !!selectedBabyId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Fetch previous day's events to catch sleep sessions that started yesterday and crossed midnight
  const { data: previousDayEvents } = useQuery({
    queryKey: ['previousDayEvents', selectedBabyId],
    queryFn: async () => {
      if (!selectedBabyId) return [];
      const yesterdayStart = startOfDay(subDays(new Date(), 1)).toISOString();
      const todayStart = startOfDay(new Date()).toISOString();
      const response = await eventApi.list(selectedBabyId, {
        startDate: yesterdayStart,
        endDate: todayStart,
        limit: 500,
      });
      return response.data?.events || [];
    },
    enabled: !!selectedBabyId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Decrypt events and calculate stats
  const [dailyStats, setDailyStats] = useState<DailyStats>({
    feeds: 0,
    nappies: 0,
    sleepMinutes: 0,
    totalFeedMl: 0,
    lastFeed: null,
    lastNappy: null,
    lastSleep: null,
  });

  // Store decrypted details for latest activity cards
  const [eventDetails, setEventDetails] = useState<Record<string, Record<string, any>>>({});

  // Calculate daily stats from today's events (with decryption for sleep duration)
  useEffect(() => {
    async function calculateStats() {
      if (!todayEvents || !Array.isArray(todayEvents) || !selectedBabyId) {
        setDailyStats({
          feeds: 0,
          nappies: 0,
          sleepMinutes: 0,
          totalFeedMl: 0,
          lastFeed: null,
          lastNappy: null,
          lastSleep: null,
        });
        return;
      }

      const stats: DailyStats = {
        feeds: 0,
        nappies: 0,
        sleepMinutes: 0,
        totalFeedMl: 0,
        lastFeed: null,
        lastNappy: null,
        lastSleep: null,
      };

      // Boundaries for today used to compute sleep overlap
      const todayMidnightMs = startOfDay(new Date()).getTime();
      const tomorrowMidnightMs = todayMidnightMs + 24 * 60 * 60 * 1000;

      // Returns the number of minutes a sleep session overlaps with today's window
      function sleepMinutesToday(startMs: number, durationMinutes: number): number {
        const endMs = startMs + durationMinutes * 60 * 1000;
        const overlapStart = Math.max(startMs, todayMidnightMs);
        const overlapEnd = Math.min(endMs, tomorrowMidnightMs);
        return overlapStart < overlapEnd ? (overlapEnd - overlapStart) / 60000 : 0;
      }

      // Get the baby's AES key for decryption
      const aesKey = await getBabyKey(selectedBabyId);

      // Track latest event per type for detail display
      const latestByType: Record<string, { timestamp: number; details: Record<string, any> }> = {};

      for (const event of todayEvents) {
        const eventDate = new Date(event.timestamp);
        const eventTime = eventDate.getTime();
        // Removed `isToday` check as backend now filters by startDate and endDate

        switch (event.type) {
          case 'FEED':
          case 'BOTTLE':
            stats.feeds++;
            if (aesKey && event.encryptedData) {
              try {
                const decrypted = await decryptEventData(event.encryptedData, aesKey);
                if (event.type === 'FEED' && typeof decrypted.amount === 'number') {
                  stats.totalFeedMl += decrypted.amount;
                } else if (event.type === 'BOTTLE') {
                  const ml = decrypted.amountConsumed ?? decrypted.amountOffered;
                  if (typeof ml === 'number') stats.totalFeedMl += ml;
                }
                // Capture details for latest activity
                if (!latestByType[event.type] || eventTime > latestByType[event.type].timestamp) {
                  if (event.type === 'FEED') {
                    latestByType[event.type] = { timestamp: eventTime, details: {
                      side: decrypted.side,
                      duration: decrypted.duration,
                    }};
                  } else {
                    const ml = decrypted.amountConsumed ?? decrypted.amountOffered;
                    latestByType[event.type] = { timestamp: eventTime, details: {
                      amountMl: typeof ml === 'number' ? ml : undefined,
                    }};
                  }
                }
              } catch { /* skip */ }
            }
            if (!stats.lastFeed || eventDate > stats.lastFeed) {
              stats.lastFeed = eventDate;
            }
            break;
          case 'NAPPY':
            stats.nappies++;
            if (aesKey && event.encryptedData) {
              try {
                const decrypted = await decryptEventData(event.encryptedData, aesKey);
                if (!latestByType['NAPPY'] || eventTime > latestByType['NAPPY'].timestamp) {
                  let nappyNotes: string | undefined;
                  if (event.encryptedNotes) {
                    try { nappyNotes = await decryptData(event.encryptedNotes, aesKey); } catch { /* skip */ }
                  }
                  latestByType['NAPPY'] = { timestamp: eventTime, details: {
                    wet: decrypted.wet,
                    dirty: decrypted.dirty,
                    rash: decrypted.rash,
                    blowout: decrypted.blowout,
                    notes: nappyNotes,
                  }};
                }
              } catch { /* skip */ }
            }
            if (!stats.lastNappy || eventDate > stats.lastNappy) {
              stats.lastNappy = eventDate;
            }
            break;
          case 'SLEEP':
            // Decrypt the event data to get actual duration
            if (aesKey && event.encryptedData) {
              try {
                const decryptedData = await decryptEventData(event.encryptedData, aesKey);
                if (decryptedData.duration && typeof decryptedData.duration === 'number') {
                  // Only count the portion of this sleep that falls within today
                  // (handles sleep that started today but extends past midnight)
                  stats.sleepMinutes += sleepMinutesToday(eventTime, decryptedData.duration);
                }
                if (!latestByType['SLEEP'] || eventTime > latestByType['SLEEP'].timestamp) {
                  latestByType['SLEEP'] = { timestamp: eventTime, details: {
                    duration: decryptedData.duration,
                  }};
                }
              } catch (err) {
                console.error('Failed to decrypt sleep event:', err);
                stats.sleepMinutes += 30;
              }
            } else {
              stats.sleepMinutes += 30;
            }
            if (!stats.lastSleep || eventDate > stats.lastSleep) {
              stats.lastSleep = eventDate;
            }
            break;
          default:
            // Capture details for other event types
            if (aesKey && event.encryptedData) {
              try {
                const decrypted = await decryptEventData(event.encryptedData, aesKey);
                if (!latestByType[event.type] || eventTime > latestByType[event.type].timestamp) {
                  latestByType[event.type] = { timestamp: eventTime, details: decrypted };
                }
              } catch { /* skip */ }
            }
            break;
        }
      }

      // Add sleep minutes from yesterday's events that crossed midnight into today
      if (previousDayEvents && Array.isArray(previousDayEvents) && aesKey) {
        for (const event of previousDayEvents) {
          if (event.type !== 'SLEEP') continue;
          if (!event.encryptedData) continue;
          try {
            const decryptedData = await decryptEventData(event.encryptedData, aesKey);
            if (decryptedData.duration && typeof decryptedData.duration === 'number') {
              const startMs = new Date(event.timestamp).getTime();
              const todayPortion = sleepMinutesToday(startMs, decryptedData.duration);
              if (todayPortion > 0) {
                stats.sleepMinutes += todayPortion;
                // Update lastSleep if this session extended into today
                const eventDate = new Date(event.timestamp);
                if (!stats.lastSleep || eventDate > stats.lastSleep) {
                  stats.lastSleep = eventDate;
                }
                if (!latestByType['SLEEP'] || startMs > latestByType['SLEEP'].timestamp) {
                  latestByType['SLEEP'] = { timestamp: startMs, details: {
                    duration: decryptedData.duration,
                  }};
                }
              }
            }
          } catch { /* skip */ }
        }
      }

      // Build details map
      const details: Record<string, Record<string, any>> = {};
      for (const [type, entry] of Object.entries(latestByType)) {
        details[type] = entry.details;
      }
      setEventDetails(details);
      setDailyStats(stats);
    }

    calculateStats();
  }, [todayEvents, previousDayEvents, selectedBabyId]);

  const selectedBaby = selectedBabyId ? decryptedBabies.get(selectedBabyId) : null;

  // Compute gestational week for pregnancy profiles
  const gestationalInfo = useMemo(() => {
    if (!selectedBaby || selectedBaby.profileType !== 'PREGNANCY' || !selectedBaby.dueDate) return null;
    const today = new Date();
    const due = new Date(selectedBaby.dueDate);
    const weeksUntilDue = differenceInWeeks(due, today);
    const week = Math.max(1, Math.min(40, 40 - weeksUntilDue));
    const daysIntoWeek = Math.abs(differenceInWeeks(today, due) % 1 * 7);
    return { week, totalWeeks: 40, daysIntoWeek: Math.floor(daysIntoWeek) };
  }, [selectedBaby]);

  // Pregnancy daily stats (computed from todayEvents without decryption)
  const pregnancyStats = useMemo(() => {
    if (!todayEvents || !Array.isArray(todayEvents) || selectedBaby?.profileType !== 'PREGNANCY') return null;
    const stats = { symptoms: 0, kicks: 0, contractions: 0, appointments: 0, totalKicks: 0 };
    for (const event of todayEvents) {
      switch (event.type) {
        case 'SYMPTOM': stats.symptoms++; break;
        case 'KICK_COUNT': stats.kicks++; break;
        case 'CONTRACTION': stats.contractions++; break;
        case 'APPOINTMENT': stats.appointments++; break;
      }
    }
    return stats;
  }, [todayEvents, selectedBaby?.profileType]);

  if (loadingBabies) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!babies || babies.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-16">
          <span className="text-6xl">👶</span>
          <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Welcome to myTotTot!
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            What are you tracking?
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <Link
              to="/app/baby/create"
              className="flex-1 card p-6 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-all border-2 border-transparent hover:border-primary-400"
            >
              <span className="text-5xl block mb-3">👶</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Baby
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Track feeds, nappies, sleep, and more for your newborn
              </p>
            </Link>
            <Link
              to="/app/pregnancy/new"
              className="flex-1 card p-6 text-center hover:shadow-lg hover:border-pink-300 dark:hover:border-pink-600 transition-all border-2 border-transparent hover:border-pink-400"
            >
              <span className="text-5xl block mb-3">🤰</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Pregnancy
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Track symptoms, appointments, kicks, and your pregnancy journey
              </p>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Baby selector */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2 items-center">
        {Array.from(decryptedBabies.values()).map((baby) => (
          <button
            key={baby.id}
            onClick={() => setSelectedBabyId(baby.id)}
            className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
              selectedBabyId === baby.id
                ? baby.profileType === 'PREGNANCY'
                  ? 'bg-pink-500 text-white'
                  : 'bg-primary-600 text-white'
                : baby.profileType === 'PREGNANCY'
                  ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            {baby.profileType === 'PREGNANCY' ? '🤰 ' : ''}{baby.name}
          </button>
        ))}
        <Link
          to="/app/baby/new"
          className="px-4 py-2 rounded-full whitespace-nowrap border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
        >
          + Add Profile
        </Link>
      </div>

      {/* Header */}
      {selectedBaby && (
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4 flex-1">
            {/* Profile Photo */}
            {profilePhotoUrl && (
              <img
                src={profilePhotoUrl}
                alt={selectedBaby.name}
                className="w-16 h-16 rounded-full object-cover border-4 border-primary-100 dark:border-primary-900 shadow-md"
              />
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedBaby.name}'s Dashboard
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {selectedBaby.profileType === 'PREGNANCY'
                  ? `Due ${format(new Date(selectedBaby.dueDate || ''), 'dd MMM yyyy')}`
                  : selectedBaby.dateOfBirth
                    ? `${formatDistanceToNow(selectedBaby.dateOfBirth, { addSuffix: false })} old`
                    : ''}
              </p>
              {/* Gestational progress bar for pregnancy */}
              {gestationalInfo && (
                <div className="mt-3 max-w-xs">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-pink-600 dark:text-pink-400">
                      Week {gestationalInfo.week} of {gestationalInfo.totalWeeks}
                    </span>
                    <span className="text-xs text-gray-400">
                      {gestationalInfo.week < 40 ? `${40 - gestationalInfo.week} weeks to go` : 'Any day now!'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-pink-400 to-pink-600 transition-all"
                      style={{ width: `${Math.min(100, (gestationalInfo.week / gestationalInfo.totalWeeks) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <Link
            to={selectedBaby.profileType === 'PREGNANCY' ? `/app/pregnancy/${selectedBabyId}` : `/app/baby/${selectedBabyId}`}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex-shrink-0"
          >
            Edit Profile
          </Link>
        </div>
      )}

      {/* Medical Disclaimer - Pregnancy profiles */}
      {selectedBaby?.profileType === 'PREGNANCY' && (
        <div className="mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex gap-3 items-start">
          <span className="text-amber-500 text-lg flex-shrink-0">&#9888;</span>
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Not Medical Advice
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              myTotTot is a tracking tool, not a medical device. Always consult your GP, midwife, or
              obstetrician for medical advice. If you experience severe pain, heavy bleeding, reduced
              fetal movement, or any concerning symptoms, seek immediate medical attention.
            </p>
          </div>
        </div>
      )}

      {/* Baby Has Arrived — Conversion Banner */}
      {selectedBaby?.profileType === 'PREGNANCY' && gestationalInfo && gestationalInfo.week >= 36 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30 border border-pink-300 dark:border-pink-700 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎉</span>
            <div className="flex-1">
              <p className="font-semibold text-pink-800 dark:text-pink-200">
                Baby has arrived?
              </p>
              <p className="text-sm text-pink-700 dark:text-pink-300">
                Convert this pregnancy profile to a baby profile to start tracking feeds, nappies, and sleep.
              </p>
            </div>
            <Link
              to={`/app/pregnancy/${selectedBabyId}`}
              className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors"
            >
              Convert
            </Link>
          </div>
        </div>
      )}

      {/* Daily Stats */}
      {selectedBaby && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Today's Summary
          </h2>
          {selectedBaby.profileType === 'PREGNANCY' ? (
            /* Pregnancy summary cards */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to={`/app/baby/${selectedBabyId}/history?types=SYMPTOM`}
                className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
              >
                <span className="text-3xl">🤢</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {pregnancyStats?.symptoms ?? 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Symptoms</p>
              </Link>
              <Link
                to={`/app/baby/${selectedBabyId}/history?types=KICK_COUNT`}
                className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
              >
                <span className="text-3xl">🦶</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {pregnancyStats?.kicks ?? 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Kick Counts</p>
              </Link>
              <Link
                to={`/app/baby/${selectedBabyId}/history?types=CONTRACTION`}
                className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
              >
                <span className="text-3xl">⏱️</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {pregnancyStats?.contractions ?? 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Contractions</p>
              </Link>
              <Link
                to={`/app/baby/${selectedBabyId}/history?types=APPOINTMENT`}
                className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
              >
                <span className="text-3xl">🏥</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {pregnancyStats?.appointments ?? 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Appointments</p>
              </Link>
            </div>
          ) : (
            /* Baby summary cards */
          <div className="grid grid-cols-3 gap-4">
            <Link
              to={`/app/baby/${selectedBabyId}/history?types=FEED`}
              className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
            >
              <span className="text-3xl">🍼</span>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {dailyStats.feeds}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Feeds</p>
              {dailyStats.totalFeedMl > 0 && (
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">
                  {dailyStats.totalFeedMl}ml
                  {selectedBaby?.dailyFeedTargetMinMl && selectedBaby?.dailyFeedTargetMaxMl
                    ? ` / ${selectedBaby.dailyFeedTargetMinMl}–${selectedBaby.dailyFeedTargetMaxMl}ml`
                    : selectedBaby?.dailyFeedTargetMinMl
                      ? ` / ${selectedBaby.dailyFeedTargetMinMl}ml`
                      : ''}
                </p>
              )}
              {dailyStats.totalFeedMl > 0 && selectedBaby?.dailyFeedTargetMinMl && (() => {
                const min = selectedBaby.dailyFeedTargetMinMl!;
                const max = selectedBaby.dailyFeedTargetMaxMl ?? min;
                const total = dailyStats.totalFeedMl;
                const pct = Math.min(100, (total / max) * 100);
                const barColor = total < min
                  ? 'bg-blue-500'
                  : total <= max
                    ? 'bg-green-500'
                    : 'bg-amber-500';
                return (
                  <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${barColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                );
              })()}
              {dailyStats.lastFeed && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Last: {format(dailyStats.lastFeed, 'h:mm a')}
                </p>
              )}
            </Link>
            <Link
              to={`/app/baby/${selectedBabyId}/history?types=NAPPY`}
              className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
            >
              <span className="text-3xl">🧷</span>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {dailyStats.nappies}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Nappies</p>
              {dailyStats.lastNappy && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Last: {format(dailyStats.lastNappy, 'h:mm a')}
                </p>
              )}
            </Link>
            <Link
              to={`/app/baby/${selectedBabyId}/history?types=SLEEP`}
              className="card p-4 text-center hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-shadow cursor-pointer"
            >
              <span className="text-3xl">😴</span>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {Math.floor(dailyStats.sleepMinutes / 60)}h {dailyStats.sleepMinutes % 60}m
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sleep</p>
              {dailyStats.lastSleep && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Last: {format(dailyStats.lastSleep, 'h:mm a')}
                </p>
              )}
            </Link>
          </div>
          )}
        </div>
      )}

      {/* Active Timers */}
      {selectedBabyId && (getActiveTimer(selectedBabyId, 'FEED') || getActiveTimer(selectedBabyId, 'SLEEP') || getActiveTimer(selectedBabyId, 'UPRIGHT') || getActiveTimer(selectedBabyId, 'TUMMY') || getActiveTimer(selectedBabyId, 'KICK_COUNT') || getActiveTimer(selectedBabyId, 'CONTRACTION')) && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Active Timers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getActiveTimer(selectedBabyId, 'FEED') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'FEED')!}
                babyName={selectedBaby?.name}
              />
            )}
            {getActiveTimer(selectedBabyId, 'SLEEP') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'SLEEP')!}
                babyName={selectedBaby?.name}
              />
            )}
            {getActiveTimer(selectedBabyId, 'UPRIGHT') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'UPRIGHT')!}
                babyName={selectedBaby?.name}
              />
            )}
            {getActiveTimer(selectedBabyId, 'TUMMY') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'TUMMY')!}
                babyName={selectedBaby?.name}
              />
            )}
            {getActiveTimer(selectedBabyId, 'KICK_COUNT') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'KICK_COUNT')!}
                babyName={selectedBaby?.name}
              />
            )}
            {getActiveTimer(selectedBabyId, 'CONTRACTION') && (
              <TimerWidget
                timer={getActiveTimer(selectedBabyId, 'CONTRACTION')!}
                babyName={selectedBaby?.name}
              />
            )}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Log
        </h2>

        {selectedBaby?.profileType === 'PREGNANCY' ? (
          <>
            {/* Pregnancy quick actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {/* Kick Count Timer */}
              {selectedBabyId && (
                <QuickTimerButton
                  babyId={selectedBabyId}
                  type="KICK_COUNT"
                  onTimerClick={(timer: ActiveTimer) => {
                    const params = new URLSearchParams({
                      type: 'KICK_COUNT',
                      fromTimer: timer.id,
                    });
                    navigate(`/app/baby/${selectedBabyId}/event/add?${params.toString()}`);
                  }}
                />
              )}

              {/* Contraction Timer */}
              {selectedBabyId && (
                <QuickTimerButton
                  babyId={selectedBabyId}
                  type="CONTRACTION"
                  onTimerClick={(timer: ActiveTimer) => {
                    const params = new URLSearchParams({
                      type: 'CONTRACTION',
                      fromTimer: timer.id,
                    });
                    navigate(`/app/baby/${selectedBabyId}/event/add?${params.toString()}`);
                  }}
                />
              )}

              {/* Symptom Log */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=SYMPTOM` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
              >
                <span className="text-2xl">🤢</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Symptom
                </span>
              </Link>

              {/* Appointment */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=APPOINTMENT` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
              >
                <span className="text-2xl">🏥</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Appointment
                </span>
              </Link>

              {/* Blood Pressure */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=BLOOD_PRESSURE` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                <span className="text-2xl">🩺</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  BP
                </span>
              </Link>

              {/* Ultrasound */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=ULTRASOUND` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
              >
                <span className="text-2xl">📷</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ultrasound
                </span>
              </Link>

              {/* Weight (already exists as WEIGHT type) */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=WEIGHT` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
              >
                <span className="text-2xl">⚖️</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Weight
                </span>
              </Link>

              {/* Reminders */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/reminders` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-violet-100 dark:bg-violet-900/30 hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors"
              >
                <span className="text-2xl">📅</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reminders
                </span>
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Baby quick actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {/* Feed Timer */}
              {selectedBabyId && (
                <QuickTimerButton
                  babyId={selectedBabyId}
                  type="FEED"
                  onTimerClick={(timer: ActiveTimer) => {
                    const params = new URLSearchParams({
                      type: 'FEED',
                      fromTimer: timer.id,
                    });
                    navigate(`/app/baby/${selectedBabyId}/event/add?${params.toString()}`);
                  }}
                />
              )}

              {/* Sleep Timer */}
              {selectedBabyId && (
                <QuickTimerButton
                  babyId={selectedBabyId}
                  type="SLEEP"
                  onTimerClick={(timer: ActiveTimer) => {
                    const params = new URLSearchParams({
                      type: 'SLEEP',
                      fromTimer: timer.id,
                    });
                    navigate(`/app/baby/${selectedBabyId}/event/add?${params.toString()}`);
                  }}
                />
              )}

              {/* Tummy Time Timer */}
              {selectedBabyId && (
                <QuickTimerButton
                  babyId={selectedBabyId}
                  type="TUMMY"
                  onTimerClick={(timer: ActiveTimer) => {
                    const params = new URLSearchParams({
                      type: 'TUMMY_TIME',
                      fromTimer: timer.id,
                      tummySeconds: '0',
                      headUpSeconds: '0',
                    });
                    navigate(`/app/baby/${selectedBabyId}/event/add?${params.toString()}`);
                  }}
                />
              )}

              {/* Nappy quick log (no timer) */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=NAPPY` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
              >
                <span className="text-2xl">🧷</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nappy
                </span>
              </Link>

              {/* Reminders Link */}
              <Link
                to={selectedBabyId ? `/app/baby/${selectedBabyId}/reminders` : '#'}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
              >
                <span className="text-2xl">📅</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reminders
                </span>
              </Link>

              {/* More Events toggle button */}
              <button
                onClick={() => setShowMoreEvents(!showMoreEvents)}
                className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
                  showMoreEvents
                    ? 'bg-primary-100 dark:bg-primary-900 ring-2 ring-primary-500'
                    : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-2xl">{showMoreEvents ? '✕' : '➕'}</span>
                <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {showMoreEvents ? 'Less' : 'More'}
                </span>
              </button>
            </div>

            {/* Secondary actions (expandable) */}
            {showMoreEvents && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                {SECONDARY_EVENT_TYPES.map((type) => {
                  const info = EVENT_TYPE_INFO[type]!;
                  return (
                    <Link
                      key={type}
                      to={selectedBabyId ? `/app/baby/${selectedBabyId}/event/new?type=${type}` : '#'}
                      className={`card p-4 text-center hover:shadow-md transition-shadow ${info.color}`}
                    >
                      <span className="text-3xl">{info.icon}</span>
                      <p className="mt-2 font-medium text-gray-900 dark:text-white">
                        {info.label}
                      </p>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Latest events */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Latest Activity
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(latestEvents || {})
          .filter(([, event]) => event != null)
          .sort(([, a]: [string, any], [, b]: [string, any]) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          .map(([type, event]: [string, any]) => {
          const info = EVENT_TYPE_INFO[type] || { icon: '📋', label: type, color: 'bg-gray-100' };

          // Build detail string from decrypted data
          const detail = eventDetails[type];
          let detailText = '';
          if (detail) {
            if (type === 'FEED') {
              const parts: string[] = [];
              if (detail.side) parts.push(detail.side.charAt(0).toUpperCase() + detail.side.slice(1) + ' side');
              if (detail.duration && typeof detail.duration === 'number') {
                const h = Math.floor(detail.duration / 60);
                const m = detail.duration % 60;
                parts.push(h > 0 ? `${h}h ${m}m` : `${m}m`);
              }
              detailText = parts.join(' · ');
            } else if (type === 'BOTTLE') {
              if (detail.amountMl) detailText = `${detail.amountMl}ml`;
            } else if (type === 'NAPPY') {
              const parts: string[] = [];
              if (detail.wet && detail.dirty) parts.push('Wet & Dirty');
              else if (detail.wet) parts.push('Wet');
              else if (detail.dirty) parts.push('Dirty');
              if (detail.rash) parts.push('Rash noted');
              if (detail.blowout) parts.push('💥 Blowout');
              if (detail.notes) parts.push(detail.notes);
              detailText = parts.join(' · ');
            } else if (type === 'SLEEP') {
              if (detail.duration && typeof detail.duration === 'number') {
                const h = Math.floor(detail.duration / 60);
                const m = detail.duration % 60;
                detailText = h > 0 ? `${h}h ${m}m` : `${m}m`;
              }
            }
          }

          return (
            <div key={type} className={`card p-4 ${info.color}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{info.icon}</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{info.label}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                    </p>
                    {detailText && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {detailText}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* View all / Health */}
      {selectedBabyId && (
        <div className="mt-6 flex justify-center gap-6">
          <Link
            to={`/app/baby/${selectedBabyId}/history`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
          >
            View All History →
          </Link>
          <Link
            to={`/app/baby/${selectedBabyId}/health`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
          >
            {selectedBaby?.profileType === 'PREGNANCY' ? 'Pregnancy Timeline →' : 'Health Metrics →'}
          </Link>
        </div>
      )}
    </div>
  );
}
